import type { ComponentProps, ReactNode } from 'react';
import { Skeleton } from 'boneyard-js/react';
import { isBoneyardBuild, useSkel } from './dev-toggle';

/**
 * Capture against the live page instead of the deterministic fixture.
 *
 * `?realbones=1` inside a boneyard build. Local copy rather than an export from
 * `dev-toggle` so that module's surface stays exactly what the seven suites that
 * mock it already provide.
 */
function isRealDataCapture(): boolean {
  if (typeof window === 'undefined' || !isBoneyardBuild()) return false;
  return new URLSearchParams(window.location.search).has('realbones');
}

type BoneyardSkeletonProps = Omit<
  ComponentProps<typeof Skeleton>,
  'children' | 'fallback' | 'fixture' | 'loading'
> & {
  children: ReactNode;
  fallback: ReactNode;
  /** Dev/capture-only. Callers gate it with import.meta.env.DEV so fixture
   * modules (mock-data + fixture components) fall out of the prod bundle. */
  fixture?: ReactNode;
  loading: boolean;
  /**
   * Whether the dev `?skeleton=1` override may force this skeleton on. Defaults
   * to true. Pagination skeletons set this to false: they only exist while the
   * next page is in flight, and forcing them for a preview drew a stray card
   * past the end of an otherwise fully rendered list. Capture mode still mounts
   * them, because that is the only way their bones get recorded.
   */
  previewable?: boolean;
};

export function BoneyardSkeleton({
  children,
  fallback,
  fixture,
  loading,
  previewable = true,
  ...skeletonProps
}: BoneyardSkeletonProps) {
  const forced = useSkel(loading);
  const realCapture = isRealDataCapture();
  const active = previewable ? forced : loading || isBoneyardBuild();

  // Capture normally records the deterministic fixture (mock-data.ts). With
  // `?realbones=1` it records the live page instead, so the bones match what
  // staging actually renders rather than a hand-curated approximation of it.
  const slot = realCapture || !active ? children : (fixture ?? fallback);

  // In prod the fixture is undefined (DEV-gated at call sites), so the loading
  // children slot falls back to the gray-box fallback to keep bone height.
  return (
    <Skeleton
      {...skeletonProps}
      loading={active}
      fixture={realCapture ? undefined : fixture}
      fallback={fallback}
    >
      {slot}
    </Skeleton>
  );
}
